// client/src/lib/fileUpload.js
//
// Orchestrates the upload flow against /api/files/* (see server/lib/fileStorage.js
// and the routes in server/index.js). The server never sees file bytes — this
// hashes the file, asks the server for a presigned R2 upload slot (which may
// come back as an instant "reused" hit if this distributor already has the
// identical file), PUTs directly to R2, then confirms.
//
// Usage:
//   const file = await uploadFile(selectedFile, { kind: 'art', clientId, label }, token);
//   // file.id, file.url, file.reused, etc.

import { api } from '../api';

// SHA-256 of the file's bytes via the browser's native Web Crypto API — no
// library needed, works in every browser this app already supports. This is
// the actual dedup key server-side: identical bytes (even re-selected under
// a different filename) collapse into the same record; any edit at all
// produces a different hash and is treated as a brand new file.
export async function hashFile(file) {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
}

// opts: { kind: 'art'|'mockup'|'po_pdf'|'other', clientId, label, replacesFileId }
export async function uploadFile(file, opts, token) {
  const contentHash = await hashFile(file);
  const client = api.withToken(token);

  const { file: record, uploadUrl, reused } = await client.presignFile({
    filename: file.name,
    mimeType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    contentHash,
    kind: opts.kind || 'other',
    clientId: opts.clientId || null,
    label: opts.label || null,
    replacesFileId: opts.replacesFileId || null,
  });

  if (reused) {
    // Identical bytes already on file for this distributor — nothing to
    // upload, the existing record is handed back as-is.
    return { ...record, reused: true };
  }

  // PUT straight to R2. Deliberately NOT using api.js's req() helper here —
  // that always sends Content-Type: application/json and JSON-stringifies
  // the body, which is wrong for raw file bytes going to a presigned S3 URL.
  const putResp = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!putResp.ok) {
    throw new Error(`Upload to storage failed (${putResp.status})`);
  }

  const confirmed = await client.confirmFile(record.id);
  return { ...confirmed, reused: false };
}
